import {downloadFile} from '@domain/file-download'
import {
  downloadRefractoryBulkTemplate,
  useImportRefractoryBulk,
  useValidateRefractoryBulk,
  type RefractoryBulkUpdateValidationResponse
} from '@domain/refractory-product-catalog'
import {useFundamentalsLanguage} from '@hconnect/common/hooks'
import {
  Alert,
  Button,
  ControlledDragAndDropFiles,
  H1,
  Icon,
  Loading,
  Modal,
  ModalFooter,
  ModalTitle
} from '@hm/fundamental'
import {mdiCheck, mdiClose, mdiInformationOutline} from '@mdi/js'
import {useState} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'
import {useRefraModal} from '../../providers'

import noFileIllustration from './no-file.svg'
import {RefraCatalogUploadPreviewTable} from './RefraCatalogUploadPreviewTable'

const infoAlertClassName =
  '!rounded border border-primary_faded_20 [&>div:last-child]:min-w-0 [&>div:last-child]:flex-1'

export const RefraCatalogUploadModal: React.FC = () => {
  const language = useFundamentalsLanguage()
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const {closeModal} = useRefraModal()
  const validateBulk = useValidateRefractoryBulk()
  const importBulk = useImportRefractoryBulk()
  const [file, setFile] = useState<File | undefined>()
  const [validation, setValidation] = useState<RefractoryBulkUpdateValidationResponse>()
  const [downloadError, setDownloadError] = useState(false)

  const isBusy = validateBulk.isPending || importBulk.isPending
  const hasValidationErrors = validation?.rows.some((row) => row.errors.length > 0) ?? false
  const canConfirm = Boolean(file && validation && !hasValidationErrors && !isBusy)

  const onFileChange = (files: File[]) => {
    const nextFile = files[0]
    setFile(nextFile)
    setValidation(undefined)

    if (nextFile) {
      validateBulk.mutate(nextFile, {
        onSuccess: setValidation
      })
    }
  }

  const onDownloadTemplate = async () => {
    setDownloadError(false)
    try {
      const template = await downloadRefractoryBulkTemplate()
      downloadFile(template, 'refractory-materials-template.xlsx')
    } catch {
      setDownloadError(true)
    }
  }

  const onConfirm = () => {
    if (!file || !canConfirm) return
    importBulk.mutate(file, {
      onSuccess: () => {
        closeModal()
      }
    })
  }

  const onClose = () => {
    validateBulk.reset()
    importBulk.reset()
    setFile(undefined)
    setValidation(undefined)
    closeModal()
  }

  return (
    <Modal
      open
      onClose={onClose}
      isExitEnabled={!importBulk.isPending}
      width="wide"
      className="[&>div]:p-0"
      data-cy="refra-catalog-upload-modal"
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="refra-catalog-upload-title"
        className="flex max-h-[calc(100dvh-2rem)] flex-col overflow-hidden rounded-lg bg-container_background text-foreground"
      >
        <ModalTitle className="flex min-h-[80px] shrink-0 items-center px-6 !pt-0">
          <span id="refra-catalog-upload-title">{t('refraCatalog.upload.title')}</span>
        </ModalTitle>

        <div className="min-h-0 overflow-y-auto px-4 pb-6">
          <div className="flex flex-col gap-4">
            <Alert
              variant="info"
              className={infoAlertClassName}
              data-cy="refra-catalog-upload-template-alert"
            >
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-3">
                  <Icon
                    path={mdiInformationOutline}
                    size="large"
                    className="shrink-0 text-primary"
                  />
                  <div className="min-w-0 text-16px leading-6">
                    <p className="font-semibold">{t('refraCatalog.upload.templateTitle')}</p>
                    <p>{t('refraCatalog.upload.templateDescription')}</p>
                  </div>
                </div>
                <Button
                  type="button"
                  size="large"
                  className="shrink-0 self-start sm:self-auto"
                  onClick={onDownloadTemplate}
                  disabled={isBusy}
                  data-cy="refra-catalog-upload-template-button"
                >
                  {t('refraCatalog.upload.downloadTemplate')}
                </Button>
              </div>
            </Alert>

            {downloadError && <Alert variant="error">{t('error.downloadTemplate')}</Alert>}

            <p className="flex min-h-[60px] items-center px-2 py-4 text-16px leading-6">
              {t('refraCatalog.upload.description')}
            </p>

            <ControlledDragAndDropFiles
              name="refractory-bulk-file"
              aria-label={t('refraCatalog.upload.title')}
              multiple={false}
              value={file ? [file] : []}
              onChange={(files) =>
                onFileChange(files.filter((entry): entry is File => entry instanceof File))
              }
              language={language}
              disabled={isBusy}
              hint={t('refraCatalog.upload.fileHint')}
              className="!min-h-[108px] !gap-3 !rounded-lg border-border_highlight_10 !p-4 [&>ul:empty]:hidden [&>div>div]:items-center [&>div>div>div>span:first-child]:px-4 [&>div>div>div>span:first-child]:py-3"
              data-cy="refra-catalog-upload-file"
            />

            {!file && (
              <div
                className="flex min-h-[300px] flex-col items-center justify-center gap-4 py-6 text-center"
                data-cy="refra-catalog-upload-empty"
              >
                <img src={noFileIllustration} alt="" className="max-w-full" />
                <H1 className="!text-[26px]">{t('refraCatalog.upload.noFileTitle')}</H1>
                <p className="text-16px leading-6 text-foreground_faded">
                  {t('refraCatalog.upload.noFileDescription')}
                </p>
              </div>
            )}

            {validateBulk.isPending && (
              <div
                className="flex min-h-[240px] flex-col items-center justify-center gap-4 py-6"
                data-cy="refra-catalog-upload-loading"
              >
                <Loading size="large" />
                <span className="text-16px text-foreground_faded">
                  {t('refraCatalog.upload.analyzing')}
                </span>
              </div>
            )}

            {validateBulk.isError && <Alert variant="error">{t('error.validateUpload')}</Alert>}

            {validation && (
              <div className="flex flex-col gap-6">
                <Alert
                  variant="info"
                  className={infoAlertClassName}
                  data-cy="refra-catalog-upload-summary"
                >
                  <div className="flex items-center gap-3">
                    <Icon
                      path={mdiInformationOutline}
                      size="large"
                      className="shrink-0 text-primary"
                    />
                    <span className="text-16px leading-6">
                      {t('refraCatalog.upload.summary', {
                        total: validation.totalCount,
                        new: validation.newCount,
                        updated: validation.updatedCount
                      })}
                    </span>
                  </div>
                </Alert>
                <RefraCatalogUploadPreviewTable rows={validation.rows} />
              </div>
            )}

            {importBulk.isError && <Alert variant="error">{t('error.importUpload')}</Alert>}
          </div>
        </div>

        <ModalFooter className="shrink-0 flex-wrap items-center border-t border-border !p-4">
          <Button
            type="button"
            variant="tertiary"
            colorClass="text-foreground"
            size="large"
            icon={mdiClose}
            onClick={onClose}
            disabled={importBulk.isPending}
          >
            {t('refraCatalog.upload.cancelUpload')}
          </Button>
          <Button
            type="button"
            size="large"
            icon={mdiCheck}
            onClick={onConfirm}
            disabled={!canConfirm}
            data-cy="refra-catalog-upload-confirm"
          >
            {importBulk.isPending ? t('refraCatalog.upload.confirming') : t('common.confirm')}
          </Button>
        </ModalFooter>
      </section>
    </Modal>
  )
}
